import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Accordion from '../atoms/Accordion';
import Ranks, { RanksType } from '../atoms/Ranks';
import Player from '../types/Player';
import Team from '../types/Team';
import Position from '../types/Position';
import get from 'lodash/get';
import { getEventName } from '../services/event-names-and-formats';
import { RanksComparisonGroup } from './RanksTooltip';
import { UpdatedPlayerPPIStat, UpdatedPlayerPPIStats as UpdatedPlayerPPIStatsType, YearlyPPIStats } from '../types/PlayerPPIStats';

interface PPIChartsDataProps {
    className?: string;
    player?: Player;
    collegeTeam?: Team;
    nflTeam?: Team;
    position?: Position;
    onYearClick?: (year: number) => void;
    isNFL?: boolean;
    updatedPPIStats?: UpdatedPlayerPPIStat;
}

interface YearPPIView {
  year: number;
  ppis: UpdatedPlayerPPIStatsType[];
}

const EMPTY_STATS = {
  percentile: 0,
  playerRank: 0,
  playerCount: 0,
};

function normalizeStatsItem(stats?: { percentile: number | null; playerRank: number | null; playerCount: number | null } | null) {
  return {
    percentile: Number(stats?.percentile) || 0,
    playerRank: Number(stats?.playerRank) || 0,
    playerCount: Number(stats?.playerCount) || 0,
  };
}

function toStats(stats: YearlyPPIStats['overallPPI']) {
  return {
  hsClassStats: normalizeStatsItem(stats?.hsClassStats) ?? EMPTY_STATS,
  div1Stats: normalizeStatsItem(stats?.div1Stats) ?? EMPTY_STATS,
  teamStats: normalizeStatsItem(stats?.teamStats) ?? EMPTY_STATS,
  conferenceStats: normalizeStatsItem(stats?.conferenceStats) ?? EMPTY_STATS,
  nflDraftStats: normalizeStatsItem(stats?.nflDraftStats) ?? EMPTY_STATS,
  };
}

function getUtilizationType(utilization: number, PPIType: string): string {
  switch (PPIType) {
    case 'Overall PPI':
      if (utilization >= 100) return 'High';
      if (utilization >= 60) return 'Medium';
      return 'Low';
      break;
    case 'Passing PPI':
      if (utilization >= 100) return 'High';
      if (utilization >= 60) return 'Medium';
      return 'Low';
      break;
    case 'Rushing PPI':
      if (utilization >= 150) return 'High';
      if (utilization >= 80) return 'Medium';
      return 'Low';
      break;
    case 'Receiving PPI':
      if (utilization >= 150) return 'High';
      if (utilization >= 80) return 'Medium';
      return 'Low';
      break;
    case 'Blocking PPI':
      if (utilization >= 500) return 'High';
      if (utilization >= 200) return 'Medium';
      return 'Low';
      break;
    case 'Defense PPI':
      if (utilization >= 500) return 'High';
      if (utilization >= 200) return 'Medium';
      return 'Low';
      break;
    default:
      return 'Low';

  }
}
function getVersatilityType(versatilityIndex: number): string {
  if (versatilityIndex >= 2.8) return 'High';
  if (versatilityIndex >= 1.4) return 'Medium';
  return 'Low';
}

const STOP_RATE_LOW = 6;
const STOP_RATE_HIGH = 10;
const MIN_STOP_RATE_PLAYS = 40;
// Critical Tackle Rates
const TACKLE_RATE_HIGH = 15;
const TACKLE_RATE_LOW = 5;
// Critical CPOE Rates
const CPOE_RATE_HIGH = 10;
const CPOE_RATE_LOW = -10;
const MIN_CPOE_RATE_PLAYS = 1;
// Critical Passing Rating Against
const PASSER_RATING_AGAINST_HIGH = 100;
const PASSER_RATING_AGAINST_LOW = 75;
const MIN_PASSER_RATING_AGAINST_PLAYS = 1;




function getMetricIcon({
  value,
  lowThreshold,
  highThreshold,
  plays,
  minimumPlays,
  isReverseIcon = false
}: {
  value?: number | null;
  lowThreshold?: number;
  highThreshold?: number;
  plays?: number | null;
  minimumPlays?: number;
  isReverseIcon?: boolean;
}): string {
  const metricValue = Number(value);

  if (!Number.isFinite(metricValue)) {
    return '';
  }

  if (typeof minimumPlays === 'number') {
    const playCount = Number(plays);
    if (!Number.isFinite(playCount) || playCount < minimumPlays) {
      return '';
    }
  }

  if (isReverseIcon) {
    if (typeof highThreshold === 'number' && metricValue >= highThreshold) return '❌';
    if (typeof lowThreshold === 'number' && metricValue <= lowThreshold) return '✅';
  }

  if (typeof highThreshold === 'number' && metricValue >= highThreshold) return '✅';
  if (typeof lowThreshold === 'number' && metricValue <= lowThreshold) return '❌';

  return '↔️';
}

function getOverallStopRateLine(yearStat: YearlyPPIStats): string {
  const icon = getMetricIcon({
    value: yearStat.ctOverallStopRate,
    lowThreshold: STOP_RATE_LOW,
    highThreshold: STOP_RATE_HIGH,
    plays: yearStat.plays,
    minimumPlays: MIN_STOP_RATE_PLAYS,
  });
  return icon ? `${icon} ${yearStat.ctOverallStopRate}% Overall Stop Rate ℹ️` : '';
}

function toPPIItems(yearStat: YearlyPPIStats): UpdatedPlayerPPIStatsType[] {
  const items: UpdatedPlayerPPIStatsType[] = [];
  if (yearStat.overallPPI) {
  const stats = toStats(yearStat.overallPPI);
  items.push({
    name: 'Overall PPI',
    value: Number(yearStat.overallPPI.ppi) || 0,
    ...stats,
    factors: [],
    percentile: Number(yearStat.overallPercentile) || 0,
    utilization: 0,
    utilizationType: getUtilizationType(Number(yearStat.rushingUtilization) || 0, 'Overall PPI'),
    totalCount: yearStat.overallTotalCount,
    rank: yearStat.overallRank,
    position: yearStat.position,
  });
  }

  if (yearStat.passingPPI) {
  const stats = toStats(yearStat.passingPPI);
  const passingUtilization = Number(yearStat.passingUtilization) || 0;
  console.log('yearStat.passingPercentile', yearStat.passingPercentile)
  items.push({
    name: 'Passing PPI',
    value: Number(yearStat.passingPPI.ppi) || 0,
    ...stats,
    factors: [
      { name: 'Efficiency', value: Number(yearStat.passingEfficiency) || 0, total: 5 },
      { name: 'Touchdowns', value: Number(yearStat.passingTouchdowns) || 0, total: 5 },
      { name: 'Explosiveness', value: Number(yearStat.passingExplosiveness) || 0, total: 5 },
      { name: 'INTs', value: Number(yearStat.passingInt) || 0, total: 5 },
    ],
    percentile: Number(yearStat.passingPercentile) || 0,
    utilization: passingUtilization,
    utilizationType: getUtilizationType(passingUtilization, 'Passing PPI'),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `Played the #${yearStat.pfPassingHardestDefense} hardest offenses (out of ${yearStat.pfPassingTotalPlayers} players).`,
      `Played on the #${yearStat.pfPassingBestOffense} best defense (out of ${yearStat.pfPassingTotalPlayers} players).`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.passingTotalCount,
    rank: yearStat.passingRank,
    position: yearStat.position
  });
  }

  if (yearStat.rushingPPI) {
  const stats = toStats(yearStat.rushingPPI);
  const rushingUtilization = Number(yearStat.rushingUtilization) || 0;
  items.push({
    name: 'Rushing PPI',
    value: Number(yearStat.rushingPPI.ppi) || 0,
    ...stats,
    factors: [
    { name: 'Power', value: Number(yearStat.rushingPower) || 0, total: 5 },
    { name: 'Efficiency', value: Number(yearStat.rushingEfficiency) || 0, total: 5 },
    { name: 'Explosiveness', value: Number(yearStat.rushingExplosiveness) || 0, total: 5 },
    ],
    percentile: Number(yearStat.rushingPercentile) || 0,
    utilization: rushingUtilization,
    utilizationType: getUtilizationType(rushingUtilization, 'Rushing PPI'),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfRushingHardestDefense ? `Played the #${yearStat.pfRushingHardestDefense} hardest offenses (out of ${yearStat.pfRushingTotalPlayers} players).` : ''}`,
      `${yearStat.pfRushingBestOffense ? `Played on the #${yearStat.pfRushingBestOffense} best defense (out of ${yearStat.pfRushingTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.rushingTotalCount,
    rank: yearStat.rushingRank,
    position: yearStat.position
  });
  }

  if (yearStat.receivingPPI) {
  const stats = toStats(yearStat.receivingPPI);
  const receivingUtilization = Number(yearStat.receivingUtilization) || 0;
  items.push({
    name: 'Receiving PPI',
    value: Number(yearStat.receivingPPI.ppi) || 0,
    ...stats,
    factors: [
    { name: 'Reliability', value: Number(yearStat.receivingReliability) || 0, total: 5 },
    { name: 'Impactfulness', value: Number(yearStat.receivingImpactfulness) || 0, total: 5 },
    { name: 'Explosiveness', value: Number(yearStat.receivingExplosiveness) || 0, total: 5 },
    ],
    percentile: Number(yearStat.receivingPercentile) || 0,
    utilization: receivingUtilization,
    utilizationType: getUtilizationType(receivingUtilization, 'Receiving PPI'),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfReceivingHardestDefense ? `Played the #${yearStat.pfReceivingHardestDefense} hardest offenses (out of ${yearStat.pfReceivingTotalPlayers} players).` : ''}`,
      `${yearStat.pfReceivingBestOffense ? `Played on the #${yearStat.pfReceivingBestOffense} best defense (out of ${yearStat.pfReceivingTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.receivingTotalCount,
    rank: yearStat.receivingRank,
    position: yearStat.position
  });
  }

  if (yearStat.blockPPI) {
  const stats = toStats(yearStat.blockPPI);
  items.push({
    name: 'Blocking PPI',
    value: Number(yearStat.blockPPI.ppi) || 0,
    ...stats,
    factors: [
      { name: 'Run Blocking', value: Number(yearStat.runBlockPpi) || 0, total: 5 },
      { name: 'Pass Blocking', value: Number(yearStat.passBlockPpi) || 0, total: 5 },
    ],
    percentile: Number(yearStat.blockingPercentile) || 0,
    utilization: yearStat.blockingSnaps,
    utilizationType: getUtilizationType(yearStat.blockingSnaps, 'Blocking PPI'),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfBlockingHardestDefense ? `Played the #${yearStat.pfDefensiveHardestOffense} hardest offenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`,
      `${yearStat.pfBlockingHardestDefense ? `Played the #${yearStat.pfBlockingHardestDefense} hardest offenses (out of ${yearStat.pfBlockingTotalPlayers} players).` : ''}`,
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.blockingTotalCount,
    rank: yearStat.blockingRank,
    position: yearStat.position
  });
  }

  if (yearStat.defensePPI) {
  const stats = toStats(yearStat.defensePPI);
  items.push({
    name: 'Defense PPI',
    value: Number(yearStat.defensePPI.ppi) || 0,
    ...stats,
    factors: [
      { name: 'Run Defense', value: Number(yearStat.runDefensePpi) || 0, total: 5 },
      { name: 'Pass Coverage', value: Number(yearStat.passCoveragePpi) || 0, total: 5 },
      { name: 'Pass Rush', value: Number(yearStat.passRushPpi) || 0, total: 5 },
    ],
    percentile: Number(yearStat.defensePercentile) || 0,
    utilization: yearStat.plays,
    utilizationType: getUtilizationType(yearStat.plays, 'Defense PPI'),
    teamFactors: [
      `${yearStat.tfDefensiveHardestOffense ? `Played the #${yearStat.tfDefensiveHardestOffense} hardest offenses (out of ${yearStat.tfDefensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfDefensiveBestDefense ? `Played on the #${yearStat.tfDefensiveBestDefense} best defenses (out of ${yearStat.tfDefensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfDefensiveHardestOffense ? `Played the #${yearStat.pfDefensiveHardestOffense} hardest offenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`,
      `${yearStat.pfDefensiveBestDefense ? `Played on the #${yearStat.pfDefensiveBestDefense} best defenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.defenseTotalCount,
    rank: yearStat.defenseRank,
    position: yearStat.position,
    versatilityIndex: yearStat.versatilityIndex,
    criticalFactors:[
      `${ yearStat.ctPasserRatingAgainst ? `${getMetricIcon({
         value: yearStat.ctPasserRatingAgainst,
         lowThreshold: PASSER_RATING_AGAINST_LOW,
          highThreshold: PASSER_RATING_AGAINST_HIGH,
          plays: yearStat.plays,
          minimumPlays: MIN_PASSER_RATING_AGAINST_PLAYS,
          isReverseIcon: true,
         })} ${yearStat.ctPasserRatingAgainst} Passer Rating Against – ${yearStat.ctCompletionsAgainst} for ${yearStat.ctAttemptsAgainst} (${yearStat.ctCompletionPercentageAgainst}%), ${yearStat.ctPassYardsAgainst} yds, ${yearStat.ctTouchdownsAgainst} TDs, ${yearStat.ctInterceptions} INTs` : ''}`,
      `${ yearStat.ctCpoe ? `${getMetricIcon({
        value: yearStat.ctCpoe,
        lowThreshold: CPOE_RATE_LOW,
        highThreshold: CPOE_RATE_HIGH,
        plays: yearStat.plays,
        minimumPlays: MIN_CPOE_RATE_PLAYS,
        isReverseIcon: true,
       })} ${yearStat.ctCpoe > 0 ? '+' : ''}${yearStat.ctCpoe}% CPOE Against ℹ️` : ''}`,
      `${ yearStat.ctOverallTackleRate ? `${getMetricIcon({ 
        value: yearStat.ctOverallTackleRate,
        lowThreshold: TACKLE_RATE_LOW,
        highThreshold: TACKLE_RATE_HIGH,
        plays: yearStat.plays,
        minimumPlays: MIN_STOP_RATE_PLAYS,
       })} ${yearStat.ctOverallTackleRate}% Overall Tackle Rate ℹ️` : ''}`,
      `${getOverallStopRateLine(yearStat)}`,
      `${ yearStat.ctPrimaryCoveragePlays ? `⚠️ Limited passes in primary coverage (${yearStat.ctPrimaryCoveragePlays}).` : '⚠️ Limited passes in primary coverage'}`,
      `${ yearStat.ctPassRushPlays ? `⚠️ Limited pass rush opportunities (${yearStat.ctPassRushPlays}).` : '⚠️ Limited pass rush opportunities'}`,
    ].filter(line => line.trim() !== ''),
    
    defenseOverallPpi: yearStat.defenseOverallPpi,
    defenseOverallSafetyPpi: yearStat.defenseOverallSafetyPpi,
    defenseOverallCornerPpi: yearStat.defenseOverallCornerPpi,
    defenseOverallOlbPpi: yearStat.defenseOverallOlbPpi,
    defenseOverallMlbPpi: yearStat.defenseOverallMlbPpi,
    defenseOverallOlbEdgePpi: yearStat.defenseOverallOlbEdgePpi,
    defenseOverallIlPpi: yearStat.defenseOverallIlPpi,
    runDefensePpi: yearStat.runDefensePpi,
    runDefenseSafetyPpi: yearStat.runDefenseSafetyPpi,
    runDefenseCornerPpi: yearStat.runDefenseCornerPpi,
    runDefenseOlbPpi: yearStat.runDefenseOlbPpi,
    runDefenseMlbPpi: yearStat.runDefenseMlbPpi,
    runDefenseOlbEdgePpi: yearStat.runDefenseOlbEdgePpi,
    runDefenseIlPpi: yearStat.runDefenseIlPpi,
    passRushPpi: yearStat.passRushPpi,
    passRushSafetyPpi: yearStat.passRushSafetyPpi,
    passRushCornerPpi: yearStat.passRushCornerPpi,
    passRushOlbPpi: yearStat.passRushOlbPpi,
    passRushMlbPpi: yearStat.passRushMlbPpi,
    passRushOlbEdgePpi: yearStat.passRushOlbEdgePpi,
    passRushIlPpi: yearStat.passRushIlPpi,
    passCoveragePpi: yearStat.passCoveragePpi,
    passCoverageSafetyPpi: yearStat.passCoverageSafetyPpi,
    passCoverageCornerPpi: yearStat.passCoverageCornerPpi,
    passCoverageOlbPpi: yearStat.passCoverageOlbPpi,
    passCoverageMlbPpi: yearStat.passCoverageMlbPpi,
    passCoverageOlbEdgePpi: yearStat.passCoverageOlbEdgePpi,
    passCoverageIlPpi: yearStat.passCoverageIlPpi,
    plays: yearStat.plays,
    pctPlaySafety: yearStat.pctPlaySafety,
    pctPlayCorner: yearStat.pctPlayCorner,
    pctPlayOlb: yearStat.pctPlayOlb,
    pctPlayMlb: yearStat.pctPlayMlb,
    pctPlayOlbEdge: yearStat.pctPlayOlbEdge,
    pctPlayIl: yearStat.pctPlayIl,
  });
  }

  return items;
}

const useStyles = makeStyles((theme: Theme) => ({
    addRemoveColumns: {
        marginBottom: theme.spacing(2),
    },
  yearsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    WebkitOverflowScrolling: 'touch',
  },
  yearButton: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 2),
    minWidth: 96,
    borderRadius: 999,
    border: '2px solid transparent',
    backgroundColor: '#f3f3f3',
    cursor: 'pointer',
    outline: 'none',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(14),
    '&:hover': {
      backgroundColor: '#e9eef9',
    },
  },
  yearButtonSelected: {
    borderColor: '#0f89e6',
    backgroundColor: '#eff6ff',
  },
  yearLabel: {
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  scorePill: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    backgroundColor: '#0f89e6',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    minWidth: 44,
    textAlign: 'center',
  },
  ppiWrapper: {
    marginTop: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: '#ffffff',
    padding: theme.spacing(2.5, 3),
    boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  ppiTopRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  ppiTopRowCentered: {
    justifyContent: 'center',
  },
  ppiScoreCircle: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    border: '8px solid #0f89e6',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5fbff',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(18),
  },
  ppiScoreLabel: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  ppiBars: {
    flex: 1,
    minWidth: 220,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  ppiBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  ppiBarLabels: {
    flex: '0 0 72px',
    fontSize: theme.typography.pxToRem(13),
  },
  ppiBarTrack: {
    flex: 1,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#e5e5e5',
    overflow: 'hidden',
  },
  ppiBarFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: '#5eba6d',
    transition: 'width 0.3s ease',
  },
  ppiBarMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
  },
  ppiInfoDot: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '1px solid #d0d0d0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
  },
  ppiSummaryRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  ppiSummaryCard: {
    flex: '1 1 0',
    minWidth: 0,
    borderRadius: 10,
    backgroundColor: '#f8f8f8',
    padding: theme.spacing(1.5, 2),
    fontSize: theme.typography.pxToRem(12),
    textAlign: 'center',
  },
  ppiSummaryTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  ppiSummaryValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
  },
  ppiSummaryValueChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 999,
    border: '1px solid transparent',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
  },
  ppiSummaryValueChipHigh: {
    backgroundColor: '#e8f7e9',
    borderColor: '#b7e3bc',
    color: '#2f7a35',
  },
  ppiSummaryValueChipMedium: {
    backgroundColor: '#fff1e3',
    borderColor: '#ffd3a3',
    color: '#9a5d14',
  },
  ppiSummaryValueChipLow: {
    backgroundColor: '#f1f3f5',
    borderColor: '#d5d8dd',
    color: '#4f5b66',
  },
  ppiSummarySub: {
    marginTop: theme.spacing(0.5),
  },
  ppiTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: theme.typography.pxToRem(12),
    marginTop: theme.spacing(1.5),
  },
  ppiTableHeadCell: {
    padding: theme.spacing(1),
    textAlign: 'left' as const,
    borderBottom: '1px solid #e0e0e0',
    fontWeight: 700,
    backgroundColor: '#fafafa',
  },
  ppiTableCell: {
    padding: theme.spacing(0.75, 1),
    borderBottom: '1px solid #f0f0f0',
    textAlign: 'center',
  },
  ppiTableHighlightCell: {
    backgroundColor: '#e8f7e9',
    fontWeight: 600,
  },
  ppiTextSection: {
    marginTop: theme.spacing(2),
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.5,
  },
  ppiTextTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  ppiBullet: {
    marginLeft: theme.spacing(2),
  },
}), { name: PPIChartsData.name });

export default function PPIChartsData(props: PPIChartsDataProps) {
  const { className, player, collegeTeam, onYearClick, isNFL, updatedPPIStats } = props;
  const classes = useStyles();

  const yearlyData: YearPPIView[] = useMemo(
    () => (updatedPPIStats?.yearlyStats || [])
      .map((yearStat) => ({
        year: Number(yearStat.year) || 0,
        ppis: toPPIItems(yearStat),
      }))
      .sort((a, b) => b.year - a.year),
    [updatedPPIStats],
  );

  console.log('yearlyData', yearlyData);

  const yearScores = useMemo(
    () => yearlyData.map((entry) => ({
      year: entry.year,
      score: Number(entry.ppis.find((ppi) => ppi.name === 'Overall PPI')?.value) || 0,
    })),
    [yearlyData],
  );

  const [selectedYear, setSelectedYear] = useState<number>(yearScores[0]?.year || 0);

  useEffect(() => {
    if (yearScores.length > 0 && !yearScores.some((entry) => entry.year === selectedYear)) {
      setSelectedYear(yearScores[0].year);
    }
  }, [yearScores, selectedYear]);

  function handleYearClick(year: number) {
    setSelectedYear(year);
    if (onYearClick) onYearClick(year);
  }

  const selectedYearData = yearlyData.find((year) => year.year === selectedYear);
  const selectedYearPPIData = selectedYearData?.ppis || [];

  function renderPPIStats (index: number, name: string, stat: UpdatedPlayerPPIStatsType) {
    return (
        <Accordion
        key={name}
        summary={`${selectedYearData?.year} ${name}`}
        expanded={index === 0}
      >
        <>
            {true && (
                <Ranks
                headerItems={[
                    { title: 'HS Class', subtitle: `(${2020})` },
                    { title: 'Division-I', subtitle: '(Since 2010)' },
                    { logo: collegeTeam?.logo247 || collegeTeam?.logoESPN || collegeTeam?.logoAlt },
                    { logo: get(collegeTeam, 'conference.iconUrl', '') },
                    { logo: '/images/nfl-draft.png' },
                ]}
                />
            )}
            <Ranks
                key={`${selectedYearData?.year} ${name}`}
                type={RanksType.PAI}
                playerId={player?.id}
                teamId={collegeTeam?.id}
                values={[
                    {
                      percentile: Math.round(get(stat, 'hsClassStats.percentile', 0)),
                      value: `${get(stat, 'hsClassStats.playerRank', 0)}/${get(stat, 'hsClassStats.playerCount', 0)}`,
                    },
                    {
                      percentile: Math.round(get(stat, 'div1Stats.percentile', 0)),
                      value: `${get(stat, 'div1Stats.playerRank', 0)}/${get(stat, 'div1Stats.playerCount', 0)}`,
                    },
                    {
                      percentile: Math.round(get(stat, 'teamStats.percentile', 0)),
                      value: `${get(stat, 'teamStats.playerRank', 0)}/${get(stat, 'teamStats.playerCount', 0)}`,
                      comparisonGroup: RanksComparisonGroup.COLLEGE_TEAM,
                    },
                    {
                      percentile: Math.round(get(stat, 'conferenceStats.percentile', 0)),
                      value: `${get(stat, 'conferenceStats.playerRank', 0)}/${get(stat, 'conferenceStats.playerCount', 0)}`,
                      comparisonGroup: RanksComparisonGroup.COLLEGE_CONFERENCE,
                    },
                    {
                      percentile: Math.round(get(stat, 'nflDraftStats.percentile', 0)),
                      value: `${get(stat, 'nflDraftStats.playerRank', 0)}/${get(stat, 'nflDraftStats.playerCount', 0)}`,
                      comparisonGroup: RanksComparisonGroup.NFL_DRAFT,
                    },
                  ]}
                bottomSpace
                noTopBorder
            />
            {/* PPI detail UI */}
            <div className={classes.ppiWrapper}>
              <div className={clsx(classes.ppiTopRow, !(stat.factors || []).length && classes.ppiTopRowCentered)}>
                {/* Score circle and factor bars */}
                <div className={classes.ppiScoreCircle}>
                  <div>{stat.value.toFixed(1)}</div>
                  <div className={classes.ppiScoreLabel}>PPI</div>
                </div>

                {!!(stat.factors || []).length && (
                  <div className={classes.ppiBars}>
                    {(stat.factors || []).map((factor, idx) => {
                      const ratio = factor.total ? factor.value / factor.total : 0;
                      const width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
                      return (
                        <div key={`${factor.name}-${idx}`} className={classes.ppiBarRow}>
                          <div className={classes.ppiBarLabels}>{factor.name}</div>
                          <div className={classes.ppiBarTrack}>
                            <div className={classes.ppiBarFill} style={{ width }} />
                          </div>
                          <div className={classes.ppiBarMeta}>
                            <span>{factor.value.toFixed(1)}</span>
                            <span className={classes.ppiInfoDot}>i</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Summary cards */}
              <div className={classes.ppiSummaryRow}>
                <div className={classes.ppiSummaryCard}>
                  <div className={classes.ppiSummaryTitle}>{selectedYearData?.year} Rank</div>
                  <div className={classes.ppiSummaryValue}>#{stat.rank ?? 0}</div>
                  <div className={classes.ppiSummarySub}>
                    out of {stat.totalCount ?? 0} {stat.position}s
                  </div>
                </div>
                <div className={classes.ppiSummaryCard}>
                  <div className={classes.ppiSummaryTitle}>Percentile</div>
                  <div className={classes.ppiSummaryValue}>
                    {Math.round(stat.percentile ?? 0)}%
                  </div>
                  <div className={classes.ppiSummarySub}>in FBS</div>
                </div>
                {
                  stat.utilization ?
                    <div className={classes.ppiSummaryCard}>
                      <div className={classes.ppiSummaryTitle}>Utilization</div>
                      <div
                        className={clsx(
                          classes.ppiSummaryValue,
                          classes.ppiSummaryValueChip,
                          (stat.utilizationType ?? 'Medium') === 'High' && classes.ppiSummaryValueChipHigh,
                          (stat.utilizationType ?? 'Medium') === 'Medium' && classes.ppiSummaryValueChipMedium,
                          (stat.utilizationType ?? 'Medium') === 'Low' && classes.ppiSummaryValueChipLow,
                        )}
                      >
                        {stat.utilizationType ?? 'Medium'}
                      </div>
                      <div className={classes.ppiSummarySub}>
                        {stat.utilization ?? 0} {(stat?.name == 'Defense PPI' || stat?.name == 'Blocking PPI') ? 'snaps' : '%'}
                      </div>
                    </div>
                  : ''
                }
                {
                  stat.versatilityIndex && 
                    <div className={classes.ppiSummaryCard}>
                      <div className={classes.ppiSummaryTitle}>Versatility</div>
                      <div
                      //  className={classes.ppiSummaryValue}
                        className={clsx(
                        classes.ppiSummaryValue,
                        classes.ppiSummaryValueChip,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'High' && classes.ppiSummaryValueChipHigh,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'Medium' && classes.ppiSummaryValueChipMedium,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'Low' && classes.ppiSummaryValueChipLow,
                      )}
                      >{getVersatilityType(stat.versatilityIndex)}</div>
                    </div>
                }
              </div>

              {/* Position group table (mocked from stat) */}
              { stat?.name == 'Defense PPI' && renderDefensePPITable(stat)}

              {/* Text sections */}
              {/* <div className={classes.ppiTextSection}>
                <div className={classes.ppiTextTitle}>Player Factors:</div>
                <div className={classes.ppiBullet}>
                  Played the #120 hardest offenses (out of 842 players).
                </div>
                <div className={classes.ppiBullet}>
                  Played on the #1 best defense (out of 842 players).
                </div>
              </div> */}

              {
                stat?.playerFactors?.length ? (
                  <div className={classes.ppiTextSection}>
                    <div className={classes.ppiTextTitle}>Player Factors:</div>
                    {(stat.playerFactors || []).map((line: string, idx: number) => (
                      <div key={idx} className={classes.ppiBullet}>{line}</div>
                    ))}
                  </div>
                ) : ''
              }

              {
                stat?.teamFactors?.length ? (
                  <div className={classes.ppiTextSection}>
                    <div className={classes.ppiTextTitle}>Team Factors:</div>
                    {(stat.teamFactors || []).map((line: string, idx: number) => (
                      <div key={idx} className={classes.ppiBullet}>{line}</div>
                    ))}
                  </div>
                ) : ''
              }

              {
                stat?.criticalFactors?.length ? (
                  <div className={classes.ppiTextSection}>
                    <div className={classes.ppiTextTitle}>Critical Factors:</div>
                    {(stat.criticalFactors || []).map((line: string, idx: number) => (
                      <div key={idx} className={classes.ppiBullet}>{line}</div>
                    ))}
                  </div>
                ) : ''
              }
            </div>
        </>
      </Accordion>
    )
  }

  function tableRowData(stat: UpdatedPlayerPPIStatsType): any[] {
    const toOneDecimalOrBlank = (value?: number | null) => {
      const num = Number(value);
      return num ? num.toFixed(1) : '';
    };

    const toPlaysOrBlank = (pct?: number | null, plays?: number | null) => {
      const pctNum = Number(pct);
      const playsNum = Number(plays);
      const total = pctNum * playsNum;
      return Number.isFinite(total) && total !== 0 ? Math.round(total) : '';
    };

    const defensePPIRowArray: any = [];
    defensePPIRowArray.push({
      position: 'Safety',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallSafetyPpi),
      plays: toPlaysOrBlank(stat.pctPlaySafety, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseSafetyPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageSafetyPpi),
      passRush: toOneDecimalOrBlank(stat.passRushSafetyPpi),
    });
    defensePPIRowArray.push({
      position: 'Corner',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallCornerPpi),
      plays: toPlaysOrBlank(stat.pctPlayCorner, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseCornerPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageCornerPpi),
      passRush: toOneDecimalOrBlank(stat.passRushCornerPpi),
    });
    defensePPIRowArray.push({
      position: 'OLB',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallOlbPpi),
      plays: toPlaysOrBlank(stat.pctPlayOlb, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseOlbPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageOlbPpi),
      passRush: toOneDecimalOrBlank(stat.passRushOlbPpi),
    });
    defensePPIRowArray.push({
      position: 'MLB',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallMlbPpi),
      plays: toPlaysOrBlank(stat.pctPlayMlb, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseMlbPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageMlbPpi),
      passRush: toOneDecimalOrBlank(stat.passRushMlbPpi),
    });
    defensePPIRowArray.push({
      position: 'DE/Edge',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallOlbEdgePpi),
      plays: toPlaysOrBlank(stat.pctPlayOlbEdge, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseOlbEdgePpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageOlbEdgePpi),
      passRush: toOneDecimalOrBlank(stat.passRushOlbEdgePpi),
    });
    defensePPIRowArray.push({
      position: 'IL',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallIlPpi),
      plays: toPlaysOrBlank(stat.pctPlayIl, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseIlPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageIlPpi),
      passRush: toOneDecimalOrBlank(stat.passRushIlPpi),
    });
    return defensePPIRowArray;
  }

  function renderDefensePPITable(stat: UpdatedPlayerPPIStatsType) {
    return (
      <table className={classes.ppiTable}>
        <thead>
          <tr>
            <th className={classes.ppiTableHeadCell}>Position Group</th>
            <th className={classes.ppiTableHeadCell}>Overall PPI</th>
            <th className={classes.ppiTableHeadCell}>Plays</th>
            <th className={classes.ppiTableHeadCell}>Run Defense</th>
            <th className={clsx(classes.ppiTableHeadCell)}>Pass Coverage</th>
            <th className={classes.ppiTableHeadCell}>Pass Rush</th>
          </tr>
        </thead>
        <tbody>
          {/* Using single stat as mock Safety row */}
          {
            tableRowData(stat).map((row: any, idx: number) => (
              <tr key={idx}>
                <td className={classes.ppiTableCell}>{row.position}</td>
                <td className={classes.ppiTableCell}>{row.overallPPI}</td>
                <td className={classes.ppiTableCell}>{row.plays}</td>
                <td className={classes.ppiTableCell}>{row.runDefense}</td>
                <td className={clsx(classes.ppiTableCell, 
                  row.passCoverage > 3.5 && classes.ppiTableHighlightCell
                  )}>{row.passCoverage}</td>
                <td className={classes.ppiTableCell}>{row.passRush}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
    )
  }

  return (
    <>
        <div className={classes.yearsContainer}>
          {yearScores.map(({ year, score }) => (
            <button
              key={year}
              type="button"
              className={clsx(
                classes.yearButton,
                selectedYear === year && classes.yearButtonSelected,
              )}
              onClick={() => handleYearClick(year)}
            >
              <span className={classes.yearLabel}>{year}</span>
              <span className={classes.scorePill}>{score}</span>
            </button>
          ))}
        </div>
        <div>
        {selectedYearPPIData.map((stat: UpdatedPlayerPPIStatsType, index: number) => (
          <div key={`${selectedYearData?.year}-${stat.name}`}>
                {renderPPIStats(index, stat.name, stat)}
            </div>
        ))}
        </div>
    </>
  );
}